import axios from 'axios';
import { Dispatch, FormEvent, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactCodeInput from 'react-code-input';
import { useDispatch, useSelector } from 'react-redux';
import { InputBackOffice } from '../../../components/common/InputBackoffice';
import Spinner from '../../../components/common/Spinner';

import {
  Button,
  Flexed,
  SecurityCodeWrapper
} from '../../../styles/GlobalComponents';

import {
  ErrorToast,
  SuccessToast
} from '../../../components/common/Toasts/Toasts';

import { P2, P3 } from '../../../styles/FontStyles';
import { color } from '../../../styles/GlobalStyles';
import { Icon, IconWarning } from '../../../styles/Icons';
import {
  Flex,
  SidebarSubtitle,
  SidebarTitle
} from '../../../styles/stylesStyled';
import { changeUserEmail } from '../../../slices/auth-slice';
import { useAppSelector } from '../../../app/hooks';

const captchaId = process.env.REACT_APP_CAPTCHA_ID;

export const ChangeEmail = ({
  setStep
}: {
  setStep: Dispatch<SetStateAction<number>>;
}) => {
  const { t } = useTranslation('settings');
  const dispatch = useDispatch();
  const [email, setEmail] = useState('');
  const [isMailSent, setIsMailSent] = useState(false);
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [newEmailCode, setNewEmailCode] = useState('');
  const [oldEmailCode, oldNewEmailCode] = useState('');
  const [code2Fa, setCode2Fa] = useState('');
  const [newCodeLoading, setNewCodeLoading] = useState(false);
  const [oldCodeLoading, setOldCodeLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const { user, token } = useAppSelector((state) => state.auth.userInfo);

  const { verificationStatus } = useAppSelector((state) => state.settings);

  const config = {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token.accessToken}`
    }
  };

  const sendNewCode = async () => {
    if (!email) {
      ErrorToast(t('email.enter_new_email'));
    } else {
      try {
        window.initGeetest4(
          {
            captchaId,
            language: 'eng',
            product: 'bind',
            riskType: 'slide',
            nativeButton: {
              width: '100%',
              marginBottom: '20px'
            }
          },
          function (captcha: any) {
            captcha.showBox();
            captcha
              .onReady(function () {})
              .onSuccess(async function () {
                setNewCodeLoading(true);
                const {
                  lot_number: lotNumber,
                  captcha_output: captchaOutput,
                  pass_token: passToken,
                  gen_time: genTime
                } = captcha.getValidate();

                await axios.post(
                  `/user/email/change/code/new`,
                  {
                    email,
                    lotNumber,
                    captchaOutput,
                    passToken,
                    genTime
                  },
                  config
                );

                SuccessToast(t('email.code_sent'));
                setNewCodeLoading(false);
                setIsMailSent(true);
              })
              .onError(function () {});
          }
        );
      } catch (error: any) {
        setNewCodeLoading(false);
        ErrorToast(error.response.data.error);
      }
    }
  };
  const sendOldCode = async () => {
    try {
      setOldCodeLoading(true);
      await axios.post(`/user/email/change/code/old`, config);

      SuccessToast(t('email.code_sent'));
      setOldCodeLoading(false);
      setIsCodeSent(true);
    } catch (error: any) {
      setOldCodeLoading(false);
      ErrorToast(error.response.data.error);
    }
  };

  const submit2faCodes = async () => {
    if (!email) {
      ErrorToast(t('email.enter_new_email'));
    } else if (!newEmailCode) {
      ErrorToast(t('email.enter_new_verification_code'));
    } else if (!oldEmailCode) {
      ErrorToast(t('email.enter_verification_code'));
    } else {
      try {
        setConfirmLoading(true);

        await axios.put(
          `/user/email/change/confirm`,
          {
            emailCodeForNewEmail: newEmailCode,
            emailCodeForOldEmail: oldEmailCode,
            twoFACode: verificationStatus ? code2Fa : null
          },
          config
        );

        dispatch(changeUserEmail(email));

        setConfirmLoading(false);

        setStep(3);
      } catch (error: any) {
        setConfirmLoading(false);
        ErrorToast(error.response.data.error);
      }
    }
  };

  const onHandleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    submit2faCodes();
  };

  return (
    <>
      {newCodeLoading || oldCodeLoading || confirmLoading ? (
        <Spinner fullScreen={false} />
      ) : (
        <>
          <Flex>
            <Icon>
              <IconWarning color='#FCBF24' />
            </Icon>
            <SidebarSubtitle> {t('email.text1')}</SidebarSubtitle>
          </Flex>
          <SidebarTitle>{t('email.new_email_verification')}</SidebarTitle>
          <form onSubmit={(e) => onHandleSubmit(e)}>
            <InputBackOffice
              id={'email'}
              type={'text'}
              placeholder={t('email.placeholder_enter_new_email')}
              name={'email'}
              value={email.replace(/\s+/g, '')}
              onChange={(e: string) => setEmail(e.replace(/\s+/g, ''))}
            />
            <Flexed between>
              <P2 bold color={color.colors.neutral700}>
                {t('email.new_email_verification_code')}
              </P2>
              <P2
                bold
                style={{ cursor: 'pointer' }}
                color={color.colors.primary900}
                onClick={() => sendNewCode()}>
                {t('email.send_code')}
              </P2>
            </Flexed>
            <SecurityCodeWrapper>
              <ReactCodeInput
                name='email code'
                inputMode='numeric'
                fields={7}
                value={newEmailCode}
                onChange={setNewEmailCode}
                type={'number'}
              />
            </SecurityCodeWrapper>

            <P3 color={color.colors.neutral500} mb='30'>
              {isMailSent && `${t('email.enter_7_digit_code')} ${email}`}
            </P3>
            <SidebarTitle>{t('email.security_verification')}</SidebarTitle>
            <Flexed between>
              <P2 bold color={color.colors.neutral700}>
                {t('email.email_code')}
              </P2>
              <P2
                bold
                style={{ cursor: 'pointer' }}
                color={color.colors.primary900}
                onClick={() => sendOldCode()}>
                {t('email.send_code')}
              </P2>
            </Flexed>
            <SecurityCodeWrapper>
              <ReactCodeInput
                name='email code'
                inputMode='numeric'
                fields={7}
                value={oldEmailCode}
                onChange={oldNewEmailCode}
                type={'number'}
              />
            </SecurityCodeWrapper>
            <P3 color={color.colors.neutral500} mb='30'>
              {isCodeSent && `${t('email.enter_7_digit_code')} ${user?.email}`}
            </P3>

            {verificationStatus && (
              <div>
                <Flexed between>
                  <P2 bold color={color.colors.neutral700}>
                    {t('email.enter_7_digit_code')}
                  </P2>
                </Flexed>
                <SecurityCodeWrapper is2Fa>
                  <ReactCodeInput
                    name='email code'
                    inputMode='numeric'
                    fields={6}
                    value={code2Fa}
                    onChange={setCode2Fa}
                    type={'number'}
                  />
                </SecurityCodeWrapper>
                <P3 color={color.colors.neutral500} mb='10'>
                  {t('email.enter_6_digit_code')}
                </P3>
              </div>
            )}

            <Button
              m='30px 0 10px 0'
              type='submit'
              disabled={
                verificationStatus
                  ? newEmailCode === '' || code2Fa === '' || oldEmailCode === ''
                  : newEmailCode === '' || oldEmailCode === ''
              }>
              {t('email.confirm')}
            </Button>
          </form>
        </>
      )}
    </>
  );
};
